import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { injectIntl, defineMessages } from 'react-intl';
import sortBy from 'lodash.sortby';
import { getCountryNameByAlpha2, getLocalizedCountries } from '../../../intl/languages';
import isEmpty from 'validator/lib/isEmpty';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import KeyboardBackspaceRoundedIcon from '@material-ui/icons/KeyboardBackspaceRounded';

import GDPRHint from '../GDPRHint';
import { FormattedMessageWrappedInSpan, MuiAutoComplete } from '../../misc';
import statesOfUsa from '../../misc/statesOfUsa';
import isEmailProper from '../../../utils/isEmailProper';

import { getFromLocalStorage, setToLocalStorage } from '../../../utils/browser-storage';

const CheckoutOrderingAddressStep = (props) => {
  const {
    viewer,
    cart,
    setUpFormCheckCallback,
    onFormErrorStateChange,
    onNextStep,
    onPreviousStep,
    isLoggedInNow,
    goToRegisterStep,
    intl,
  } = props;
  const childRef = useRef();

  let initialEmail = getFromLocalStorage('email') || '';
  let address = {
    firstName: '',
    lastName: '',
    line1: '',
    line2: '',
    postalCode: '',
    city: '',
    country: {
      alpha2: '',
      name: '',
    },
    state: '',
    phone: '',
  };

  if (viewer.account && viewer.account.profile && !cart.order) {
    // The address field on profile can be empty
    if (viewer.account.profile.addresses.edges.length >= 1) {
      address = viewer.account.profile.addresses.edges[0].node;
    }
    initialEmail = viewer.account.profile.email.trim();
  } else if (cart.order) {
    initialEmail = cart.customer.email.trim();
    if (cart.order.address) {
      address = cart.order.address;
    }
  }

  const [showInputErrorMsg, setShowInputErrorMsg] = useState({
    firstName: false,
    lastName: false,
    line1: false,
    postalCode: false,
    city: false,
    countryCode: false,
    state: false,
    email: false,
    confirmEmail: false,
  });
  const [firstName, setFirstName] = useState(address.firstName || '');
  const [lastName, setLastName] = useState(address.lastName || '');
  const [line1, setLine1] = useState(address.line1 || '');
  const [line2, setLine2] = useState(address.line2 || '');
  const [postalCode, setPostalCode] = useState(address.postalCode || '');
  const [city, setCity] = useState(address.city || '');
  const [country, setCountry] = useState({
    alpha2: address.country.alpha2 || '',
    name: address.country.name || '',
  });
  const [state, setState] = useState(address.state || '');
  const [phone, setPhone] = useState(address.phone || '');
  const [email, setEmail] = useState(initialEmail);
  const [confirmEmail, setConfirmEmail] = useState(initialEmail);

  const countryAutoCompleteOptions = useMemo(() => {
    // Sort the list of countries in alphabetical order A-Z
    const sortedCountries = sortBy(
      getLocalizedCountries(viewer.countries),
      (country) => country.name
    );
    // Create array for `Select` component
    return sortedCountries.map((country) => ({
      value: country.alpha2,
      label: country.name,
    }));
  }, [viewer.countries]);

  const stateAutoCompleteOptions = useMemo(() => (
    statesOfUsa.map((state) => ({ value: state, label: state }))
  ), []);

  const _isEmpty = (s) => isEmpty(s, { ignore_whitespace: true });

  const _emailsMatch = () => email === confirmEmail;

  const _checkInputFields = () => {
    if (
      _isEmpty(firstName) ||
      _isEmpty(lastName) ||
      _isEmpty(line1) ||
      _isEmpty(postalCode) ||
      _isEmpty(city) ||
      _isEmpty(country.alpha2) ||
      (country.alpha2 === 'US' && _isEmpty(state)) ||
      _isEmpty(email) ||
      _isEmpty(confirmEmail)
    ) {
      return false;
    }
    return true;
  };

  const getErrorText = (inputValue, name, isEmailField) => {
    if (!showInputErrorMsg[name]) return null;
    if (_isEmpty(inputValue)) {
      return intl.formatMessage(translations.inputRequiredErrorText);
    }
    if (isEmailField) {
      if (!isEmailProper(inputValue)) {
        return intl.formatMessage(translations.invalidEmailErrorText);
      }
      if (!_emailsMatch()) {
        return intl.formatMessage(translations.emailMatchErrorText);
      }
    }
    return null;
  };

  const getCountryAutoCompleteValue = () => {
    if (country.alpha2 === '' && country.name === '') return null;
    return {
      label: getCountryNameByAlpha2(country.alpha2),
      value: country.alpha2,
    };
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    onFormErrorStateChange(false);
    setShowInputErrorMsg((prev) => ({ ...prev, name: false }));
    switch (name) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'line1':
        setLine1(value);
        break;
      case 'line2':
        setLine2(value);
        break;
      case 'postalCode':
        setPostalCode(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'countryCode':
        setCountry({
          alpha2: value,
          name: getCountryNameByAlpha2(value),
        });
        break;
      case 'state':
        setState(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'confirmEmail':
        setConfirmEmail(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      default:
        break;
    }
  };

  const handleInputChangeEmail = (event) => {
    const value = event.target.value.trim();
    handleInputChange({
      target: {
        name: event.target.name,
        value: value,
      },
    });
  };

  const handleCountryChanged = (selectedCountry) => {
    setShowInputErrorMsg((prev) => ({ ...prev, countryCode: false }));
    setCountry({
      alpha2: selectedCountry ? selectedCountry.value : '',
      name: selectedCountry ? selectedCountry.label : '',
    });
  };

  const handleStateChanged = (selectedState) => {
    setShowInputErrorMsg((prev) => ({ ...prev, state: false }));
    setState(selectedState ? selectedState.value : '');
  };

  const handlePaymentButtonClicked = useCallback(() => {
    if (!_checkInputFields()) {
      setShowInputErrorMsg({
        firstName: _isEmpty(firstName),
        lastName: _isEmpty(lastName),
        line1: _isEmpty(line1),
        postalCode: _isEmpty(postalCode),
        city: _isEmpty(city),
        countryCode: _isEmpty(country.alpha2),
        email: _isEmpty(email),
        confirmEmail: _isEmpty(confirmEmail),
        state: country.alpha2 === 'US' && _isEmpty(state),
      });
      onFormErrorStateChange(true);
      return;
    }
    if (!isEmailProper(email) || !isEmailProper(confirmEmail)) {
      setShowInputErrorMsg((prev) => ({
        ...prev,
        email: true,
        confirmEmail: true,
      }));
      onFormErrorStateChange(true);
      return;
    }
    if (!_emailsMatch()) {
      setShowInputErrorMsg((prev) => ({
        ...prev,
        email: true,
        confirmEmail: true,
      }));
      onFormErrorStateChange(true);
      return;
    }
    const details = {
      firstName,
      lastName,
      line1,
      line2,
      postalCode,
      city,
      country,
      state,
      phone,
      email,
      confirmEmail,
    };
    onNextStep(details);
  }, [
  firstName, lastName, line1, line2, postalCode, city, country, state, phone, email, confirmEmail,
  onFormErrorStateChange, onNextStep
  ]);

  const handleOnPaste = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    setUpFormCheckCallback(handlePaymentButtonClicked);
    return () => {
      setToLocalStorage('email', email.trim());
    };
  }, [setUpFormCheckCallback, handlePaymentButtonClicked, email]);

  return (
    <div className="order-address-form-container" ref={childRef}>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <TextField
            value={firstName}
            name="firstName"
            autoComplete="given-name"
            fullWidth
            label={intl.formatMessage(translations.firstName)}
            onChange={handleInputChange}
            error={showInputErrorMsg.firstName}
            helperText={getErrorText(firstName, 'firstName')}
          />
        </div>
        <div className="col-xs-16 col-sm-5">
          <TextField
            value={lastName}
            name="lastName"
            autoComplete="family-name"
            fullWidth
            label={intl.formatMessage(translations.lastName)}
            onChange={handleInputChange}
            helperText={getErrorText(lastName, 'lastName')}
            error={showInputErrorMsg.lastName}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <TextField
            value={line1}
            name="line1"
            autoComplete="address-line1"
            fullWidth
            label={intl.formatMessage(translations.line1)}
            onChange={handleInputChange}
            helperText={getErrorText(line1, 'line1')}
            error={showInputErrorMsg.line1}
          />
        </div>
        <div className="col-xs-16 col-sm-5">
          <TextField
            value={line2}
            name="line2"
            autoComplete="off"
            label={intl.formatMessage(translations.line2)}
            onChange={handleInputChange}
            fullWidth
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <TextField
            value={postalCode}
            name="postalCode"
            autoComplete="postal-code"
            fullWidth
            label={intl.formatMessage(translations.postalCode)}
            onChange={handleInputChange}
            helperText={getErrorText(postalCode, 'postalCode')}
            error={showInputErrorMsg.postalCode}
          />
        </div>
        <div className="col-xs-16 col-sm-5">
          <TextField
            value={city}
            name="city"
            autoComplete="address-level2"
            fullWidth
            label={intl.formatMessage(translations.city)}
            onChange={handleInputChange}
            helperText={getErrorText(city, 'city')}
            error={showInputErrorMsg.city}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <FormControl fullWidth error={showInputErrorMsg.countryCode}>
            <MuiAutoComplete
              label={intl.formatMessage(translations.countryCode)}
              placeholder={intl.formatMessage(translations.countryCode)}
              onChange={handleCountryChanged}
              options={countryAutoCompleteOptions}
              value={getCountryAutoCompleteValue()}
            />
            <FormHelperText>
              {getErrorText(country.alpha2, 'countryCode')}
            </FormHelperText>
          </FormControl>
        </div>

        {country.alpha2 === 'US' && (
          <div className="col-xs-16 col-sm-5 ">
            <FormControl fullWidth error={showInputErrorMsg.state}>
              <MuiAutoComplete
                label={'State'}
                placeholder={'State'}
                onChange={handleStateChanged}
                options={stateAutoCompleteOptions}
                value={{ value: state, label: state }}
              />
              <FormHelperText>{getErrorText(state, 'state')}</FormHelperText>
            </FormControl>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <TextField
            value={email}
            name="email"
            autoComplete="email"
            fullWidth
            disabled={Boolean(cart.order)}
            label={intl.formatMessage(translations.email)}
            onChange={handleInputChangeEmail}
            helperText={getErrorText(email, 'email', true)}
            error={showInputErrorMsg.email}
          />
        </div>
        <div className="col-xs-16 col-sm-5">
          <TextField
            value={confirmEmail}
            name="confirmEmail"
            autoComplete="email"
            fullWidth
            label={intl.formatMessage(translations.confirmEmail)}
            onChange={handleInputChangeEmail}
            helperText={getErrorText(confirmEmail, 'confirmEmail', true)}
            onPaste={handleOnPaste}
            disabled={Boolean(cart.order)}
            error={showInputErrorMsg.confirmEmail}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-5 col-sm-offset-3">
          <TextField
            value={phone}
            name="phone"
            autoComplete="tel-national"
            fullWidth
            label={intl.formatMessage(translations.phone)}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="order-address-form-navigation col-xs-16 col-sm-5 last-sm">
          <div className={`cart-btn-next-step`}>
            <button
              className="btn-default primary-new full-width"
              onClick={handlePaymentButtonClicked}
            >
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingAddressStep.nextButton"
                defaultMessage="Proceed to payment"
              />
            </button>
          </div>
        </div>
        <div className="order-address-form-navigation col-xs-16 col-sm-5 col-sm-offset-3">
          <div
            className="navigation-back"
            onClick={() => {
              if (isLoggedInNow) {
                onPreviousStep();
              } else {
                goToRegisterStep();
              }
            }}
          >
            <KeyboardBackspaceRoundedIcon />
            {isLoggedInNow ? (
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingAddressStep.backButtonCart"
                defaultMessage="Back to cart"
              />
            ) : (
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingAddressStep.backButtonLogin"
                defaultMessage="Back to login"
              />
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-10 col-sm-offset-3">
          <GDPRHint className="mt-30 center-text" />
        </div>
      </div>
    </div>
  );
};

CheckoutOrderingAddressStep.propTypes = {
  onNextStep: PropTypes.func,
  onPreviousStep: PropTypes.func,
  cart: PropTypes.object,
  viewer: PropTypes.object,
  intl: PropTypes.object,
  isLoggedInNow: PropTypes.bool,
  goToRegisterStep: PropTypes.func,
  onFormErrorStateChange: PropTypes.func,
  setUpFormCheckCallback: PropTypes.func,
};

const translations = defineMessages({
  firstName: {
    id: 'checkoutOrderingAddressStep.input.firstName',
    defaultMessage: 'First name',
  },
  lastName: {
    id: 'checkoutOrderingAddressStep.input.lastName',
    defaultMessage: 'Last name',
  },
  email: {
    id: 'checkoutOrderingAddressStep.input.email',
    defaultMessage: 'Email',
  },
  confirmEmail: {
    id: 'checkoutOrderingAddressStep.input.confirmEmail',
    defaultMessage: 'Confirm email',
  },
  line1: {
    id: 'checkoutOrderingAddressStep.input.line1',
    defaultMessage: 'Address 1',
  },
  line2: {
    id: 'checkoutOrderingAddressStep.input.line2',
    defaultMessage: 'Address 2',
  },
  postalCode: {
    id: 'checkoutOrderingAddressStep.input.postalCode',
    defaultMessage: 'Postal code',
  },
  city: {
    id: 'checkoutOrderingAddressStep.input.city',
    defaultMessage: 'City',
  },
  countryCode: {
    id: 'checkoutOrderingAddressStep.input.countryCode',
    defaultMessage: 'Country code',
  },
  phone: {
    id: 'checkoutOrderingAddressStep.input.phone',
    defaultMessage: 'Telephone',
  },
  inputRequiredErrorText: {
    id: 'checkoutOrderingAddressStep.inputErrorText',
    defaultMessage: 'This field is required',
  },
  invalidEmailErrorText: {
    id: 'checkoutOrderingAddressStep.invalidEmailErrorText',
    defaultMessage: 'Please enter a valid email',
  },
  emailMatchErrorText: {
    id: 'checkoutOrderingAddressStep.emailMatchErrorText',
    defaultMessage: 'Both email addresses has to be the same',
  },
});

export { CheckoutOrderingAddressStep };

export default createFragmentContainer(injectIntl(CheckoutOrderingAddressStep), {
  cart: graphql`
    fragment CheckoutOrderingAddressStep_cart on Cart {
      id
      customer {
        email
      }
      order {
        id
        address {
          firstName
          lastName
          phone
          city
          postalCode
          line2
          line1
          country {
            alpha2
            name
          }
          state
        }
      }
    }
  `,
  viewer: graphql`
    fragment CheckoutOrderingAddressStep_viewer on Viewer {
      countries {
        name
        alpha2
      }
      account @include(if: $isLoggedIn) {
        profile {
          addresses(first: 10) {
            edges {
              node {
                firstName
                lastName
                phone
                city
                postalCode
                state
                line2
                line1
                country {
                  alpha2
                  name
                }
              }
            }
          }
          email
        }
      }
    }
  `,
});
